<script lang="ts" setup>
import { computed, useSlots } from "vue";
import VLoader from "../VLoader/VLoader.vue";
import type {
  IInlineButtonProps,
  IInlineButtonEmits,
} from "./VInlineButton.types";
import { responsiveClass } from "../../utilities/helpers";

const props = withDefaults(defineProps<IInlineButtonProps>(), {
  size: "l",
  type: "button",
  theme: "primary",
  tag: "button",
  iconPosition: undefined,
});

const emit = defineEmits<IInlineButtonEmits>();
const slots = useSlots() as {
  default?(): unknown;
  icon?(): unknown;
};

const hasButtonText = computed(() => {
  return !!slots.default;
});
const hasIcon = computed(() => {
  return !!slots.icon;
});

const rootClass = computed(() => [
  {
    [`pl-button-inline_${props.theme}`]: props.theme,
    [`pl-button-inline_size-${props.size}`]: props.size,
    ["pl-button-inline_loading"]: props.loading,
    ["pl-button-inline_disabled"]: props.disabled,
    [`pl-button-inline_icon-${props.iconPosition}`]:
      hasIcon.value && props.iconPosition,
  },
  ...responsiveClass("pl-button-inline_square", props.square),
]);

const onClick = (event: Event) => {
  emit("click", event);
};
</script>

<template>
  <component
    :is="tag"
    class="pl-button-inline"
    :class="rootClass"
    :disabled="disabled || loading"
    :type="type"
    @click="onClick"
  >
    <span v-if="props.loading" class="pl-button-inline__loader">
      <VLoader color="inherit" size="s" />
    </span>
    <span class="pl-button-inline__content">
      <span v-if="hasButtonText" class="pl-button-inline__text"><slot /></span>
      <span v-if="hasIcon" class="pl-button-inline__icon">
        <slot name="icon" />
      </span>
    </span>
  </component>
</template>

<style scoped lang="postcss">
.pl-button-inline {
  --pl-button-inline-display: block;

  border: none;
  background: transparent;
  cursor: pointer;
  outline: 0;
  box-sizing: border-box;
  position: relative;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: top;
  border-radius: var(--pl-button-radius-s);

  &:focus-visible {
    outline: 2px solid var(--pl-tabulation-border-focused);
    outline-offset: 2px;
  }

  &:active {
    outline: none;
  }

  &_loading {
    cursor: progress;
  }

  &_disabled {
    cursor: not-allowed;
  }

  &_size-s {
    font: var(--pl-font-body-small-accent);
    min-height: 24px;
    min-width: 24px;
  }

  &_size-l {
    font: var(--pl-font-body-large-accent);
    min-height: 28px;
    min-width: 28px;
  }

  &_primary {
    color: var(--pl-inline-button-text-primary-default);

    &:hover {
      color: var(--pl-inline-button-text-primary-hover);
    }

    &:active {
      color: var(--pl-inline-button-text-primary-pressed);
    }
  }

  &_primary&_disabled {
    color: var(--pl-inline-button-text-primary-disabled);
  }

  &_secondary {
    color: var(--pl-inline-button-text-secondary-default);

    &:hover {
      color: var(--pl-inline-button-text-secondary-hover);
    }

    &:active {
      color: var(--pl-inline-button-text-secondary-pressed);
    }
  }

  &_secondary&_disabled {
    color: var(--pl-inline-button-text-secondary-disabled);
  }

  &_invert {
    color: var(--pl-inline-button-text-invert-default);

    &:hover {
      color: var(--pl-inline-button-text-invert-hover);
    }

    &:active {
      color: var(--pl-inline-button-text-invert-pressed);
    }
  }

  &_invert&_disabled {
    color: var(--pl-inline-button-text-invert-disabled);
  }
}

.pl-button-inline__loader {
  position: absolute;
  top: 50%;
  left: 50%;
  align-items: center;
  display: flex;
  transform: translate(-50%, -50%);
}

.pl-button-inline__content {
  align-items: center;
  display: flex;
  justify-content: center;

  .pl-button-inline_loading & {
    visibility: hidden;
  }
}

.pl-button-inline__text {
  .pl-button-inline_icon-left & {
    margin-left: var(--pl-unit-x2);
  }

  .pl-button-inline_icon-right & {
    margin-right: var(--pl-unit-x2);
  }

  .pl-button-inline_square & {
    display: none;
  }

  @each $breakpoint in s, m, ml, l, xl {
    @media (--pl-viewport-from-$(breakpoint)) {
      .pl-button-inline_square-true-$(breakpoint) & {
        display: none;
      }

      .pl-button-inline_square-false-$(breakpoint) & {
        display: var(--pl-button-display);
      }
    }
  }
}

.pl-button-inline__icon {
  align-items: center;
  display: flex;
  justify-content: center;

  .pl-button-inline_icon-left & {
    order: -1;
  }
}
</style>
